import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getMe, getPlayerRank, getEloHistory } from '../api/api';


export default function Stats() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [rank, setRank] = useState(null);
  const [eloHistory, setEloHistory] = useState([]);

  
  useEffect(() => {
    if (user) {
      getMe().then(res => {
        setStats(res.data.stats);
        if (res.data.ranks) setRank({ ranks: res.data.ranks });
        getPlayerRank(res.data.user?.username || user.username)
          .then(r => setRank(r.data)).catch(() => {});
      }).catch(() => {});

      getEloHistory().then(res => {
        const history = res.data.history || [];
        const points = history.map(h => ({
          match: h.match_number,
          elo: h.elo,
          label: `Match ${h.match_number}`
        }));
        setEloHistory(points.slice(-6));
      }).catch(() => {
        setEloHistory([]);
      });
    }
  }, [user]);

  const winRate = stats ? Math.round((stats.wins / Math.max(stats.wins + stats.losses, 1)) * 100) : 0;
  const kd = stats ? (stats.deaths > 0 ? (stats.kills / stats.deaths).toFixed(2) : stats.kills) : 0;
  const rankRows = Array.isArray(rank?.ranks) ? rank.ranks : (rank ? [rank] : []);
  const primaryRank = rankRows.find((item) => item.mode === 'fivevfive') || rank?.primary || rankRows[0] || null;
  const isRanked = primaryRank?.is_ranked === true;
  const rankName = isRanked ? (primaryRank?.rank_name || 'Unranked') : 'Unranked';
  const rankStatus = isRanked
    ? `${primaryRank.elo} ELO`
    : `${primaryRank?.placement_games_played || 0}/${primaryRank?.placement_games_required || 5} placements`;

  const renderBarChart = (data, maxVal, color) => {
    return (
      <div style={s.barChart}>
        {data.map((d, i) => (
          <div key={i} style={s.barWrap}>
            <div style={s.barLabel}>{d.label}</div>
            <div style={s.barBg}>
              <div style={{
                ...s.barFill,
                height: `${(d.val / maxVal) * 100}%`,
                background: color,
                boxShadow: `0 0 8px ${color}60`,
              }}/>
            </div>
            <div style={s.barVal}>{d.val}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderLineGraph = (points) => {
    if (points.length < 2) {
      return (
        <div style={{display:'flex', alignItems:'center', justifyContent:'center', height:'120px', color:'#333', fontSize:'11px', letterSpacing:'2px'}}>
          PLAY MORE MATCHES TO SEE YOUR GRAPH
        </div>
      );
    }

    const elos = points.map(p => p.elo);
    const min = Math.min(...elos) - 50;
    const max = Math.max(...elos) + 50;
    const range = max - min;
    const w = 500;
    const h = 120;
    const stepX = w / (points.length - 1);

    const coords = points.map((p, i) => ({
      x: i * stepX,
      y: h - ((p.elo - min) / range) * h,
    }));

    const pathD = coords.map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x} ${c.y}`).join(' ');
    const areaD = `${pathD} L ${coords[coords.length-1].x} ${h} L 0 ${h} Z`;
    const trending = elos[elos.length-1] >= elos[0];
    const lineColor = trending ? '#E8192C' : '#555';

    return (
      <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={lineColor} stopOpacity="0.15"/>
            <stop offset="100%" stopColor={lineColor} stopOpacity="0"/>
          </linearGradient>
        </defs>
        <path d={areaD} fill="url(#areaGrad)"/>
        <path d={pathD} fill="none" stroke={lineColor} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"/>
        {coords.map((c, i) => (
          <circle key={i} cx={c.x} cy={c.y} r="4" fill={lineColor} stroke="#111111" strokeWidth="2"/>
        ))}
      </svg>
    );
  };

  return (
    <div style={s.root}>
      <style>{`
        @keyframes rp-page-up{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
        @media (prefers-reduced-motion: reduce){*{animation-duration:1ms!important;transition-duration:1ms!important}}
      `}</style>

      {/* HEADER */}
      <div style={s.header}>
        <div style={s.headerEyebrow}>
          <span style={s.eyebrowDot}/>
          STATISTICS
        </div>
        <div style={s.headerTitle}>Your Stats</div>
        <div style={s.headerSub}>Detailed breakdown of your performance</div>
      </div>

      {/* TOP STAT CARDS */}
      <div style={s.topStats}>
        {[
          {label: '5V5 RANK', val: rankName, color: '#E8192C', sub: rankStatus},
          {label: 'WIN RATE', val: `${winRate}%`, color: '#FF6B2B', sub: `${stats?.wins || 0}W / ${stats?.losses || 0}L`},
          {label: 'K/D RATIO', val: kd, color: '#FFD700', sub: `${stats?.kills || 0}K / ${stats?.deaths || 0}D`},
          {label: 'MATCHES', val: stats?.matches_played || 0, color: '#4169E1', sub: 'Total played'},
          {label: 'WIN STREAK', val: stats?.win_streak || 0, color: '#FF6B2B', sub: stats?.win_streak >= 3 ? `+${stats.win_streak >= 7 ? '1.35' : stats.win_streak >= 5 ? '1.25' : '1.15'}x ELO` : 'No bonus'},
          {label: 'FRAGZ', val: stats?.fragz || 0, color: '#FF6B2B', sub: 'Currency earned'},
        ].map((stat, i) => (
          <div key={i} style={s.topStat}>
            <div style={s.topStatLabel}>{stat.label}</div>
            <div style={{...s.topStatVal, color: stat.color, textShadow: `0 0 12px ${stat.color}40`}}>
              {stat.val}
            </div>
            <div style={s.topStatSub}>{stat.sub}</div>
          </div>
        ))}
      </div>

      <div style={s.grid}>

        {/* ELO HISTORY GRAPH */}
        <div style={{...s.card, gridColumn: '1 / -1'}}>
          <div style={s.cardEyebrow}>ELO PROGRESSION</div>
          <div style={s.cardTitle}>Rating History</div>
          <div style={s.graphWrap}>
  {renderLineGraph(eloHistory)}
</div>
<div style={s.graphFooter}>
  {eloHistory.map((p, i) => (
    <div key={i} style={s.graphFooterItem}>
      <div style={s.graphFooterLabel}>{p.label}</div>
      <div style={{
        ...s.graphFooterVal,
        color: i > 0 && p.elo > eloHistory[i-1].elo ? '#E8192C' : i > 0 ? '#555' : '#888'
      }}>
        {i > 0 ? (p.elo > eloHistory[i-1].elo ? '▲' : '▼') : ''} {p.elo}
      </div>
    </div>
  ))}
</div>
        </div>

        {/* WIN/LOSS BAR CHART */}
        <div style={s.card}>
          <div style={s.cardEyebrow}>RESULTS</div>
          <div style={s.cardTitle}>Win / Loss</div>
          {renderBarChart([
            {label: 'WINS', val: stats?.wins || 0},
            {label: 'LOSSES', val: stats?.losses || 0},
            {label: 'MATCHES', val: stats?.matches_played || 0},
          ], Math.max(stats?.matches_played || 1, 1), '#E8192C')}
        </div>

        {/* KD BAR CHART */}
        <div style={s.card}>
          <div style={s.cardEyebrow}>COMBAT</div>
          <div style={s.cardTitle}>Kills / Deaths</div>
          {renderBarChart([
            {label: 'KILLS', val: stats?.kills || 0},
            {label: 'DEATHS', val: stats?.deaths || 0},
          ], Math.max(stats?.kills || 1, stats?.deaths || 1, 1), '#FF6B2B')}
        </div>

        {/* RANK PROGRESS */}
        <div style={s.card}>
          <div style={s.cardEyebrow}>RANK</div>
          <div style={s.cardTitle}>Progress</div>
          {primaryRank && (
            <div style={s.rankWrap}>
              <div style={s.rankRow}>
                <img
                  src={`/ranks/${rankName.toLowerCase().replace(/ /g,'-')}.png`}
                  alt=""
                  style={{width:'48px', height:'48px', objectFit:'contain'}}
                  onError={e => e.target.style.display='none'}
                />
                <div>
                  <div style={s.rankName}>{rankName}</div>
                  <div style={s.rankElo}>{rankStatus}</div>
                </div>
              </div>
              <div style={s.rankBarBg}>
                <div style={{...s.rankBarFill, width:`${primaryRank.progress_percent || 0}%`}}/>
              </div>
              <div style={s.rankBarLabel}>
                {isRanked
                  ? (primaryRank.elo_to_next_rank > 0 ? `${primaryRank.elo_to_next_rank} ELO to next rank` : 'MAX RANK')
                  : `Complete ${primaryRank.placement_games_required || 5} placements to reveal your rank`}
              </div>
            </div>
          )}
        </div>

        {/* STREAKS */}
        <div style={s.card}>
          <div style={s.cardEyebrow}>STREAKS</div>
          <div style={s.cardTitle}>Current Form</div>
          <div style={s.streakWrap}>
            <div style={s.streakItem}>
              <div style={s.streakLabel}>WIN STREAK</div>
              <div style={{...s.streakVal, color: stats?.win_streak > 0 ? '#E8192C' : '#333',
                textShadow: stats?.win_streak > 0 ? '0 0 12px #E8192C60' : 'none'}}>
                {stats?.win_streak || 0}
              </div>
              {stats?.win_streak >= 3 && (
                <div style={s.streakBonus}>
                  +{stats.win_streak >= 7 ? '1.35' : stats.win_streak >= 5 ? '1.25' : '1.15'}x ELO BONUS
                </div>
              )}
            </div>
            <div style={s.streakDivider}/>
            <div style={s.streakItem}>
              <div style={s.streakLabel}>LOSS STREAK</div>
              <div style={{...s.streakVal, color: stats?.loss_streak > 0 ? '#555' : '#333'}}>
                {stats?.loss_streak || 0}
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

const s = {
  root: { background:'#0a0a0a', minHeight:'100vh', color:'#F0F2F5', fontFamily:"'Rajdhani','Segoe UI',sans-serif", animation:'rp-page-up 420ms cubic-bezier(0.2, 0.7, 0.2, 1) both' },

  header: { marginBottom:'32px' },
  headerEyebrow: { display:'flex', alignItems:'center', gap:'8px', fontSize:'10px', letterSpacing:'4px', color:'#E8192C', fontWeight:'700', marginBottom:'8px' },
  headerTitle: { fontFamily:"'Bebas Neue',sans-serif", fontSize:'clamp(36px,6vw,56px)', letterSpacing:'6px', color:'#F0F2F5', lineHeight:'1' },
  headerSub: { fontSize:'13px', color:'#444', letterSpacing:'1px', marginTop:'6px' },
  eyebrowDot: { width:'5px', height:'5px', borderRadius:'50%', background:'#E8192C', boxShadow:'0 0 6px #E8192C', display:'inline-block' },

  topStats: { display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(140px,1fr))', gap:'14px', marginBottom:'24px' },
  topStat: { background:'#111111', border:'1px solid #1e1e1e', borderRadius:'8px', padding:'16px', display:'flex', flexDirection:'column', gap:'4px' },
  topStatLabel: { fontSize:'9px', letterSpacing:'3px', color:'#444', fontWeight:'700' },
  topStatVal: { fontFamily:"'Bebas Neue',sans-serif", fontSize:'32px', letterSpacing:'3px', lineHeight:'1' },
  topStatSub: { fontSize:'10px', color:'#333', letterSpacing:'1px' },

  grid: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px' },
  card: { background:'#111111', border:'1px solid #1e1e1e', borderRadius:'8px', padding:'24px', display:'flex', flexDirection:'column', gap:'16px' },
  cardEyebrow: { fontSize:'9px', letterSpacing:'4px', color:'#E8192C', fontWeight:'700' },
  cardTitle: { fontFamily:"'Bebas Neue',sans-serif", fontSize:'22px', letterSpacing:'3px', color:'#F0F2F5', marginTop:'-8px' },

  graphWrap: { background:'#0a0a0a', border:'1px solid #1e1e1e', borderRadius:'6px', padding:'16px', overflow:'hidden' },
  graphFooter: { display:'flex', justifyContent:'space-between' },
  graphFooterItem: { display:'flex', flexDirection:'column', alignItems:'center', gap:'2px' },
  graphFooterLabel: { fontSize:'9px', color:'#333', letterSpacing:'1px' },
  graphFooterVal: { fontFamily:"'Bebas Neue',sans-serif", fontSize:'14px', letterSpacing:'1px' },

  barChart: { display:'flex', gap:'16px', alignItems:'flex-end', height:'140px' },
  barWrap: { display:'flex', flexDirection:'column', alignItems:'center', gap:'6px', flex:1 },
  barLabel: { fontSize:'9px', letterSpacing:'2px', color:'#444', fontWeight:'700' },
  barBg: { flex:1, width:'100%', background:'#1a1a1a', borderRadius:'4px', display:'flex', alignItems:'flex-end', overflow:'hidden' },
  barFill: { width:'100%', borderRadius:'4px', transition:'height 0.8s ease', minHeight:'4px' },
  barVal: { fontFamily:"'Bebas Neue',sans-serif", fontSize:'16px', letterSpacing:'2px', color:'#888' },

  rankWrap: { display:'flex', flexDirection:'column', gap:'12px' },
  rankRow: { display:'flex', alignItems:'center', gap:'14px' },
  rankName: { fontFamily:"'Bebas Neue',sans-serif", fontSize:'20px', letterSpacing:'3px', color:'#F0F2F5' },
  rankElo: { fontSize:'12px', color:'#FF6B2B', letterSpacing:'2px' },
  rankBarBg: { background:'#1a1a1a', borderRadius:'2px', height:'4px', overflow:'hidden' },
  rankBarFill: { background:'linear-gradient(90deg,#E8192C,#FF6B2B)', height:'100%', borderRadius:'2px', transition:'width 0.8s ease' },
  rankBarLabel: { fontSize:'10px', color:'#333', letterSpacing:'1px', textAlign:'right' },

  streakWrap: { display:'flex', alignItems:'center', gap:'20px' },
  streakItem: { flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'8px' },
  streakLabel: { fontSize:'9px', letterSpacing:'3px', color:'#444', fontWeight:'700' },
  streakVal: { fontFamily:"'Bebas Neue',sans-serif", fontSize:'56px', letterSpacing:'4px', lineHeight:'1', transition:'all 0.3s' },
  streakBonus: { fontSize:'9px', letterSpacing:'2px', color:'#FF6B2B', fontWeight:'700', background:'#1a0a03', border:'1px solid #FF6B2B30', borderRadius:'3px', padding:'3px 8px' },
  streakDivider: { width:'1px', height:'80px', background:'#1e1e1e' },
};